'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import classNames from 'classnames';
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { useContext, useEffect, useRef, useState, memo } from 'react';
import UIClickable from '../button/UIClickable';
import { FieldsetContext } from '../context/FieldsetContext';
import { callIfPossible } from '../core/Functions';
import SyntheticEvent from '../core/SyntheticEvent';
import Controllable from '../decorators/Controllable';
import ShareInput from '../decorators/ShareInput';
import UIIcon from '../icon/UIIcon';
import UITextInput from '../input/UITextInput';
import UITooltip from '../tooltip/UITooltip';
import lazyEval from '../utils/lazyEval';
import { hidden } from '../utils/propTypes/decorators';
import deprecated from '../utils/propTypes/deprecated';
import { propTypeForSizes, toShorthandSize } from '../utils/propTypes/tshirtSize';
import { computeMinimumSearchMessageOverflow } from './utils/computeMinimumSearchMessageOverflow';

const getMinimumSearchMessage = (minimumSearchCount, value, getValidationMessage) => {
  //This is deprecating minimumSearchCount, so if an getValidationMessage is present it will ignore minimumSearchCount
  if (getValidationMessage) {
    return getValidationMessage(value);
  }

  const valueLength = value ? value.length : 0;
  const charsNeeded = minimumSearchCount - valueLength;

  if (valueLength && charsNeeded > 0) {
    return I18n.text('salesUI.UISearchInput.minimumSearchMessage', {
      count: charsNeeded
    });
  }

  return null;
};

const UISearchInput = /*#__PURE__*/memo((_ref) => {
  let {
    clearable,
    getValidationMessage,
    icon,
    inputClassName,
    inputRef,
    minimumSearchCount,
    onChange,
    placeholder,
    readOnly,
    size,
    value,
    onKeyDown,
    className
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["clearable", "getValidationMessage", "icon", "inputClassName", "inputRef", "minimumSearchCount", "onChange", "placeholder", "readOnly", "size", "value", "onKeyDown", "className"]);

  const [minimumSearchMessageOverflows, setMinimumSearchMessageOverflows] = useState(false);
  const wrapperRef = useRef();
  const iconRef = useRef();
  const helpTextRef = useRef();
  const {
    size: fieldsetContextSize
  } = useContext(FieldsetContext);
  const showClearButton = clearable && value && !readOnly;
  const minimumSearchMessage = getMinimumSearchMessage(minimumSearchCount, value, getValidationMessage);

  const triggerClear = () => {
    onChange(SyntheticEvent(''));
    inputRef.current.focus();
  };

  const handleKeyDown = evt => {
    if (readOnly) return;

    if (evt.key === 'Escape') {
      triggerClear();
    }

    callIfPossible(onKeyDown, evt);
  };

  useEffect(() => {
    if (getValidationMessage || minimumSearchCount > 0) {
      setMinimumSearchMessageOverflows(computeMinimumSearchMessageOverflow(wrapperRef.current, iconRef.current, helpTextRef.current, inputRef.current));
    }
  }, [getValidationMessage, minimumSearchCount, inputRef, value // The value is used to compute the width of the text inside of the input
  ]);
  return /*#__PURE__*/_jsx(UITooltip, {
    open: !!(minimumSearchMessage && minimumSearchMessageOverflows),
    title: minimumSearchMessage,
    children: /*#__PURE__*/_jsxs("div", {
      className: classNames('private-search-control', className, toShorthandSize(size) === 'xl' && 'private-search-control--xl'),
      ref: wrapperRef,
      children: [/*#__PURE__*/_jsx(UITextInput, Object.assign({}, rest, {
        className: classNames('private-search-control__input', inputClassName),
        onKeyDown: handleKeyDown,
        placeholder: lazyEval(placeholder),
        readOnly: readOnly,
        value: value,
        onChange: onChange,
        inputRef: inputRef
      })), /*#__PURE__*/_jsxs("span", {
        className: "private-search-control__foreground",
        children: [minimumSearchMessage ? /*#__PURE__*/_jsx("span", {
          "aria-atomic": false,
          "aria-live": "polite",
          className: 'private-search-control__help-text' + (minimumSearchMessageOverflows ? " private-search-control__help-text--hidden" : ""),
          ref: helpTextRef,
          children: minimumSearchMessage
        }) : null, icon && !readOnly ? /*#__PURE__*/_jsx("span", {
          ref: iconRef,
          children: /*#__PURE__*/_jsx(UIIcon, {
            className: classNames('private-search-control__icon', showClearButton && 'private-search-control__icon--hidden', fieldsetContextSize === 'small' && 'private-search-control__icon--small'),
            name: "search"
          })
        }) : null]
      }), showClearButton ? /*#__PURE__*/_jsx(UIClickable, {
        className: 'private-search-control__clear-button' + (fieldsetContextSize === 'small' ? " private-search-control__clear-button--small" : ""),
        onClick: triggerClear,
        tabIndex: 0,
        "aria-label": I18n.text('salesUI.UISearchInput.clearButton'),
        children: /*#__PURE__*/_jsx(UIIcon, {
          name: "remove"
        })
      }) : null]
    })
  });
});
UISearchInput.propTypes = Object.assign({}, UITextInput.propTypes, {
  clearable: PropTypes.bool.isRequired,
  icon: PropTypes.bool.isRequired,
  inputClassName: PropTypes.string,
  getValidationMessage: PropTypes.func,
  minimumSearchCount: deprecated(PropTypes.number, {
    message: `UISearchInput: The \`minimumSearchCount\` prop is deprecated. Use \`getValidationMessage\` instead.`,
    key: 'UISearchInput-minimumSearchCount'
  }, 0),
  size: PropTypes.oneOfType([propTypeForSizes(['xl']), PropTypes.oneOf(['default'])]).isRequired,
  type: hidden(UITextInput.propTypes.type)
});
UISearchInput.defaultProps = Object.assign({}, UITextInput.defaultProps, {
  clearable: true,
  icon: true,
  getValidationMessage: null,
  minimumSearchCount: 0,
  placeholder: () => I18n.text('salesUI.UISearchInput.placeholder'),
  size: 'default',
  type: 'search',
  value: ''
});
UISearchInput.displayName = 'UISearchInput';
export default ShareInput(Controllable(UISearchInput));